import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ReactFlow, ReactFlowProvider, Background, BackgroundVariant, Controls,
    MarkerType, Handle, Position, applyNodeChanges, useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { AlertTriangle, Building2, ClipboardList, Compass, Database, Eye, FileText, Flag, IdCard, Target, Trash2 } from "lucide-react";
import { LAYERS, NODE_TYPES, rollStale } from "../lib/constants";

const ICONS = { avdelning: Building2, uppgift: ClipboardList, syfte: Target, system: Database, roll: IdCard, dokument: FileText, vision: Eye, malbild: Flag, strategi: Compass };
const nodeTypes = { atlas: AtlasNode };

function AtlasNode({ data, selected }) {
    const t = NODE_TYPES[data.nodeType] || NODE_TYPES.avdelning;
    const Icon = ICONS[data.nodeType] || Building2;
    return (
        <div
            data-testid={`canvas-node-${data.testid}`}
            className={`relative w-[190px] rounded-lg border bg-deepsea/90 backdrop-blur px-3 py-2.5 transition-opacity duration-200 ${selected ? "border-current shadow-[0_0_24px_rgba(56,189,248,0.35)]" : "border-border hover:border-current/50"} ${data.dimmed ? "opacity-25" : ""}`}
        >
            <Handle type="target" position={Position.Left} />
            <div className="flex items-center gap-2">
                <span className="w-1.5 h-8 rounded-full shrink-0" style={{ background: t.color }} />
                <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-slate-100 truncate">{data.label}</div>
                    <div className="font-mono text-[9px] uppercase tracking-widest" style={{ color: t.color }}>{t.label}{data.antal ? ` · ${data.antal} anställda` : ""}</div>
                </div>
                {data.stale && <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" title="Oreviderad roll – kan fokusera på fel saker" />}
                <Icon className="w-4 h-4 text-slate-500 shrink-0" />
            </div>
            {data.dammar > 0 && (
                <span className="absolute -top-2 -right-2 flex items-center gap-0.5 bg-amber-500/20 border border-amber-500/60 text-amber-400 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
                    <AlertTriangle className="w-3 h-3" />{data.dammar}
                </span>
            )}
            <Handle type="source" position={Position.Right} />
        </div>
    );
}

function Inner({ graph, layers, selection, onSelect, onMoveNode, onConnectNodes, onDropType, onTrashNode }) {
    const [nodes, setNodes] = useState([]);
    const [draggingNode, setDraggingNode] = useState(null);
    const trashRef = useRef(null);
    const { screenToFlowPosition } = useReactFlow();

    useEffect(() => {
        setNodes(graph.nodes.map((n) => ({
            id: n.id,
            type: "atlas",
            position: { x: n.x, y: n.y },
            data: {
                label: n.label,
                nodeType: n.type,
                testid: n.id,
                antal: n.antal_anstallda,
                stale: n.type === "roll" && rollStale(n.reviderad),
                dimmed: n.perspectives?.length ? !n.perspectives.some((p) => layers[p]) : false,
                dammar: graph.bottlenecks.filter((b) => b.target_type === "nod" && b.target_id === n.id && b.status !== "avvisad").length,
            },
            selected: selection?.kind === "node" && selection.id === n.id,
        })));
    }, [graph, layers, selection]);

    const edges = useMemo(() => graph.relations
        .filter((r) => layers[r.perspective])
        .map((r) => {
            const layer = LAYERS.find((l) => l.id === r.perspective);
            const color = layer?.color || "#38BDF8";
            return {
                id: r.id, source: r.source, target: r.target,
                label: r.label || undefined,
                style: { stroke: color, strokeWidth: 1.5, opacity: 0.75 },
                labelStyle: { fill: "#94A3B8", fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" },
                labelBgStyle: { fill: "#0A121D", fillOpacity: 0.9 },
                labelBgPadding: [6, 3],
                labelBgBorderRadius: 4,
                markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
                selected: selection?.kind === "relation" && selection.id === r.id,
            };
        }), [graph, layers, selection]);

    const onNodesChange = useCallback((changes) => setNodes((nds) => applyNodeChanges(changes, nds)), []);

    return (
        <div className="relative w-full h-full">
        <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onConnect={(p) => p.source !== p.target && onConnectNodes(p.source, p.target)}
            onNodeDragStart={(e, node) => setDraggingNode(node.id)}
            onNodeDragStop={(e, node) => {
                const rect = trashRef.current?.getBoundingClientRect();
                const overTrash = rect && e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
                if (overTrash && onTrashNode) onTrashNode(node.id);
                else onMoveNode(node.id, node.position);
                setDraggingNode(null);
            }}
            onNodeClick={(e, n) => onSelect({ kind: "node", id: n.id })}
            onEdgeClick={(e, ed) => onSelect({ kind: "relation", id: ed.id })}
            onPaneClick={() => onSelect(null)}
            onDrop={(e) => {
                e.preventDefault();
                const type = e.dataTransfer.getData("application/hamn-node");
                if (type) {
                    const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
                    onDropType(type, pos.x, pos.y);
                }
            }}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
            fitView
            minZoom={0.25}
            maxZoom={1.8}
            colorMode="dark"
            deleteKeyCode={null}
            data-testid="operations-map"
        >
            <Background variant={BackgroundVariant.Dots} gap={28} size={1} color="rgba(56,189,248,0.10)" />
            <Controls position="bottom-right" showInteractive={false} />
        </ReactFlow>
        <div
            ref={trashRef}
            data-testid="trash-drop-zone"
            className={`absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 rounded-xl border px-4 py-3 pointer-events-none transition-all duration-200 ${draggingNode ? "border-red-500/60 bg-red-500/15 text-red-400 scale-105 shadow-[0_0_24px_rgba(239,68,68,0.25)]" : "border-border bg-deepsea/60 text-slate-600"}`}
        >
            <Trash2 className="w-5 h-5" />
            <span className="text-xs font-medium">{draggingNode ? "Släpp för att ta bort" : "Papperskorg – dra hit överflödiga noder"}</span>
        </div>
        </div>
    );
}

export default function OperationsMap(props) {
    return (
        <ReactFlowProvider>
            <Inner {...props} />
        </ReactFlowProvider>
    );
}
